import React, { useState, useEffect } from 'react';
import { Lock, Check, Plus, AlertTriangle } from 'lucide-react';
import styles from './form.module.css';
import ParticipantQuestionImage from './ParticipantQuestionImage';
import { API_PATH, request } from '../../../config/api';

function arraysEqual(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => v === b[i]);
}

function containsProfanity(text, list) {
    if (!text || typeof text !== 'string') return false;
    const lower = text.trim().toLowerCase();
    const normalized = lower.replace(/[^\w\s]/g, '');
    const tokens = normalized.split(/\s+/).filter(Boolean);
    for (const word of list) {
        if (tokens.includes(word)) return true;
        if (normalized.includes(word)) return true;
    }
    return false;
}

const OpenTextForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true, eventCode = null }) => {
    const multipleAnswers = content.settings?.multipleAnswers === true;
    const maxResponses = multipleAnswers
        ? Math.min(20, Math.max(2, Number(content.settings?.responses) || 5))
        : 1;
    const profanityFilterOn = content.settings?.profanityFilter !== false;
    const [profanityError, setProfanityError] = useState('');
    const [profanityList, setProfanityList] = useState([]);

    useEffect(() => {
        if (!profanityFilterOn) return;
        let cancelled = false;
        (async () => {
            try {
                const url = eventCode
                    ? `${API_PATH}/join/profanity-words?eventCode=${encodeURIComponent(eventCode)}`
                    : `${API_PATH}/join/profanity-words`;
                const res = await request(url);
                const data = await res.json().catch(() => ({}));
                if (!cancelled && Array.isArray(data?.words)) setProfanityList(data.words);
            } catch {
                if (!cancelled) setProfanityList([]);
            }
        })();
        return () => { cancelled = true; };
    }, [profanityFilterOn, eventCode]);

    const getInitialTexts = () => {
        if (userAnswer?.texts && Array.isArray(userAnswer.texts) && userAnswer.texts.length > 0) {
            return [...userAnswer.texts];
        }
        if (userAnswer?.text != null && String(userAnswer.text).trim() !== '') {
            return [String(userAnswer.text).trim()];
        }
        return [''];
    };
    const [texts, setTexts] = useState(getInitialTexts);

    useEffect(() => {
        if (userAnswer?.texts && Array.isArray(userAnswer.texts) && userAnswer.texts.length > 0) {
            setTexts([...userAnswer.texts]);
        } else if (userAnswer?.text != null && String(userAnswer.text).trim() !== '') {
            setTexts([String(userAnswer.text).trim()]);
        } else if (userAnswer == null) setTexts(['']);
    }, [userAnswer]);

    const updateText = (i, val) => {
        setTexts(t => t.map((x, j) => (j === i ? val : x)));
        if (profanityError) setProfanityError('');
    };
    const addResponse = () => { if (texts.length < maxResponses) setTexts(t => [...t, '']); };

    const handleSubmit = () => {
        if (isLocked) return;
        setProfanityError('');
        const filtered = texts.map(t => t.trim()).filter(Boolean);
        if (profanityFilterOn && profanityList.length > 0 && filtered.some(t => containsProfanity(t, profanityList))) {
            setProfanityError('One or more responses are not allowed. Please edit and try again.');
            return;
        }
        if (maxResponses === 1) {
            onSubmit({ text: filtered[0] || '' });
        } else {
            onSubmit({ texts: filtered });
        }
    };

    const currentTexts = texts.map(t => t.trim()).filter(Boolean);
    const submittedTexts = userAnswer?.texts && Array.isArray(userAnswer.texts)
        ? userAnswer.texts
        : userAnswer?.text != null ? [String(userAnswer.text).trim()] : [];
    const hasSubmitted = userAnswer != null;
    const optionsDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const isUnchanged = hasSubmitted && arraysEqual(currentTexts, submittedTexts);
    const submitLabel = !hasSubmitted
        ? (isPreview ? 'Submit' : isLocked ? 'Voting Locked' : 'Submit')
        : !allowUpdateResponse || isUnchanged ? <>Submitted <Check size={14} strokeWidth={3} aria-hidden /></> : 'Update response';
    const submitDisabled = isPreview || isLocked || !currentTexts.length || (hasSubmitted && (!allowUpdateResponse || isUnchanged));

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Share your thoughts'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden /> Locked</span>}
            </div>

            {content.settings?.description && content.settings?.descText && (
                <div className={styles.description}>
                    {content.settings.descText}
                </div>
            )}

            {content.settings?.imageUrl && (
                <ParticipantQuestionImage imageUrl={content.settings.imageUrl} alt="Question visual" />
            )}

            {texts.map((text, i) => (
                <textarea
                    key={i}
                    className={styles.textArea}
                    placeholder={multipleAnswers ? `Response ${i + 1}…` : 'Type your response here…'}
                    value={text}
                    onChange={e => updateText(i, e.target.value)}
                    disabled={optionsDisabled}
                    rows={3}
                />
            ))}
            {multipleAnswers && texts.length < maxResponses && (
                <button
                    type="button"
                    className={styles.submitBtnSecondary}
                    onClick={addResponse}
                    disabled={optionsDisabled}
                >
                    <Plus size={16} aria-hidden /> Add another response
                </button>
            )}
            {profanityError && (
                <p className={styles.profanityError} role="alert">
                    <AlertTriangle size={16} aria-hidden /> {profanityError}
                </p>
            )}
            <button
                className={styles.submitBtn}
                onClick={handleSubmit}
                disabled={submitDisabled}
            >
                {submitLabel}
            </button>
        </div>
    );
};

export default OpenTextForm;
